import React, { useState, useEffect, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionLaunch } from '../lib/useSessionLaunch';
import { HarmonyLogoCompact } from '../components/HarmonyLogo';
import { ExpenseProvider, useExpense } from '../context/ExpenseContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LayoutDashboard, Receipt, FileBarChart, Settings, ClipboardList, LogOut, ChevronDown, User, AlertTriangle, RefreshCw } from 'lucide-react';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';

// Tab Components
import ExpenseDashboard from '../components/expense/ExpenseDashboard';
import ExpenseList from '../components/expense/ExpenseList';
import ExpenseReports from '../components/expense/ExpenseReports';
import ExpenseSettings from '../components/expense/ExpenseSettings';
import ExpenseAuditLogs from '../components/expense/ExpenseAuditLogs';

// Error Boundary to catch any rendering errors in the app
class ExpenseManagerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ExpenseManager Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center max-w-md">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              The Expense Manager encountered an error. Please try refreshing the page.
            </p>
            <p className="text-sm text-red-500 mb-4 font-mono bg-red-50 p-2 rounded">
              {this.state.error?.message || 'Unknown error'}
            </p>
            <Button 
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                window.location.reload();
              }}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reload App
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ExpenseManagerContent = ({ username, role }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchLOV } = useExpense();
  
  // Set page title and favicon
  usePageMeta(PAGE_META.EXPENSE_MANAGER.title, PAGE_META.EXPENSE_MANAGER.favicon);
  
  // Bug 3 Fix: Get active tab from URL params, default to 'dashboard'
  const tabFromUrl = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  

  // Bug 3 Fix: Update URL when tab changes to persist across refresh
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Update URL params without losing existing params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    fetchLOV();
  }, [fetchLOV]);

  const handleLogout = () => {
    window.close();
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="expense-manager-app">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <HarmonyLogoCompact size={32} />
              <div>
                <h1 className="font-semibold text-lg text-foreground">Expense Manager</h1>
                <p className="text-xs text-muted-foreground">Harmony Apps</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="hidden md:flex">
              <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="bg-transparent gap-1">
                  <TabsTrigger 
                    value="dashboard" 
                    className="gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                    data-testid="tab-dashboard"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </TabsTrigger>
                  <TabsTrigger 
                    value="expenses" 
                    className="gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                    data-testid="tab-expenses"
                  >
                    <Receipt className="w-4 h-4" />
                    Expenses
                  </TabsTrigger>
                  <TabsTrigger 
                    value="reports" 
                    className="gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                    data-testid="tab-reports"
                  >
                    <FileBarChart className="w-4 h-4" />
                    Reports
                  </TabsTrigger>
                  <TabsTrigger 
                    value="settings" 
                    className="gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                    data-testid="tab-settings"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </TabsTrigger>
                  <TabsTrigger 
                    value="audit" 
                    className="gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
                    data-testid="tab-audit"
                  >
                    <ClipboardList className="w-4 h-4" />
                    Audit Logs
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getInitials(username)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{username}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{username}</p>
                  <p className="text-xs text-muted-foreground capitalize">{role.replace('_', ' ')}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Tabs */}
          <div className="md:hidden pb-2 -mx-4 px-4 overflow-x-auto">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="bg-transparent gap-1 w-max">
                <TabsTrigger value="dashboard" className="text-xs px-3">
                  <LayoutDashboard className="w-3 h-3 mr-1" />
                  Dashboard
                </TabsTrigger>
                <TabsTrigger value="expenses" className="text-xs px-3">
                  <Receipt className="w-3 h-3 mr-1" />
                  Expenses
                </TabsTrigger>
                <TabsTrigger value="reports" className="text-xs px-3">
                  <FileBarChart className="w-3 h-3 mr-1" />
                  Reports
                </TabsTrigger>
                <TabsTrigger value="settings" className="text-xs px-3">
                  <Settings className="w-3 h-3 mr-1" />
                  Settings
                </TabsTrigger>
                <TabsTrigger value="audit" className="text-xs px-3">
                  <ClipboardList className="w-3 h-3 mr-1" />
                  Audit
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'dashboard' && <ExpenseDashboard />}
        {activeTab === 'expenses' && <ExpenseList />}
        {activeTab === 'reports' && <ExpenseReports />}
        {activeTab === 'settings' && <ExpenseSettings role={role} />}
        {activeTab === 'audit' && <ExpenseAuditLogs />}
      </main>
    </div>
  );
};

const ExpenseManagerApp = () => {
  // Signed in from the visitor's own session (see useSessionLaunch).
  const { token, username, role } = useSessionLaunch();

  return (
    <ExpenseManagerErrorBoundary>
      <ExpenseProvider token={token}>
        <ExpenseManagerContent username={username} role={role} />
      </ExpenseProvider>
    </ExpenseManagerErrorBoundary>
  );
};

export default ExpenseManagerApp;
